import React from "react";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import Form from "react-bootstrap/Form";
import _ from "lodash";
import {
    useCreateDashboardStore,
    CreateChartState,
    CHART_NAME_PATTERN,
    CHART_ERROR_MESSAGE,
} from ".";
import CreateChartModal from "./modal/CreateChart";
import { ChartSource } from "constant/chart";
import { WidgetItem } from "types";
import { getChartSourceFromWidgetType, getWidgetConfigByWidgetType, WidgetType } from "../utils/widgetConfig";

type AttributeType = {
    color: [number, string][];
};

const defaultColors: Partial<Record<WidgetType, string[]>> = {
    [WidgetType.SUBSCRIPTION_CHART_TOKEN_DONUT]: [
        "#EE9EC6",
        "#F6C39D",
        "#FFEFB5",
        "#A2DFD8",
        "#52BFD4",
    ],
    [WidgetType.SUBSCRIPTION_CHART_TOKEN_STACKED]: [
        "#EE9EC6",
        "#F6C39D",
        "#FFEFB5",
        "#A2DFD8",
        "#52BFD4",
    ],
};

const OtherChartCreateData: CreateChartState = {
    title: "Creating New Widget(s)",
    data: [],
    columns: [
        {
            key: "name",
            name: "Widget Name",
        },
    ],
    transform: (data: any, widgetType: WidgetType) => {

        let attributes: AttributeType[] = [];
        const colors = (data.colors as string[]) ?? [];
        if (colors.length) {
            attributes = [
                {
                    color: colors.map((color: string, index) => [index, color]),
                },
            ];
        }

        return {
            attributes,
            source: ChartSource.SUBSCRIPTION,
            chart: getChartSourceFromWidgetType(widgetType).chart,
            name: data.name,
        };
    },
    validate() {
        return this.data.map((item) => ({
            path: "",
            isValid: CHART_NAME_PATTERN.test(item.name.trim()),
            message: CHART_ERROR_MESSAGE,
        }));
    },
};

const DashboardCreateSubscriptionWidget = ({
    widget,
}: {
    widget: WidgetItem;
}) => {
    const widgetType = widget.widgetType;
    const widgetConfig = getWidgetConfigByWidgetType(widgetType);

    const defaultFormValues = {
        name: widgetConfig.defaultWidgetName,
        colors: defaultColors[widgetType],
    };
    const [formValues, setFormValues] = React.useState<{
        [key: string]: any;
    }>({
        ...defaultFormValues,
    });
    const { createModalShow, setCreateModalShow, createData } =
        useCreateDashboardStore();

    React.useEffect(() => {
        useCreateDashboardStore.setState({
            createData: {
                ...OtherChartCreateData,
                data: [{ ...formValues }],
            },
        });
    }, [formValues]);

    const handleFormChange = ({
        event,
        field,
        value,
    }: {
        event?: React.ChangeEvent<any>;
        field?: string;
        value?: any;
    }) => {
        const values: { [key: string]: any } = {};
        if (event) {
            const { name, type, checked, value } = event.target;
            values[name] = type === "checkbox" ? checked : value;
        } else if (field) {
            values[field] = value;
        }

        const newFormValues = {
            ...formValues,
            ...values,
        };
        setFormValues(newFormValues);
    };

    return (
        <React.Fragment>
            <Row className="mt-4">
                <Col sm={12}>
                    <Form onChange={(event) => handleFormChange({ event })}>
                        <Form.Group>
                            <Form.Label htmlFor="name">Widget Name</Form.Label>
                            <Form.Control
                                id="name"
                                name="name"
                                type="text"
                                placeholder="Widget Name"
                                defaultValue={widgetConfig.defaultWidgetName}
                            />
                        </Form.Group>
                    </Form>
                </Col>
            </Row>
            <CreateChartModal
                show={createModalShow && !_.isEmpty(createData)}
                onHide={() => setCreateModalShow(false)}
            />
        </React.Fragment>
    );
};

export default React.memo(DashboardCreateSubscriptionWidget);
